import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import api, { mediaUrl } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import BlogEditor from "./BlogEditor";

export default function BlogPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post object or 'new' or null
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/blog");
      setPosts(res.data);
    } catch (e) {
      toast({ title: "Couldn't load posts", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    await api.delete(`/admin/blog/${id}`);
    setPosts((x) => x.filter((p) => p.id !== id));
    toast({ title: "Article deleted" });
  };

  if (editing) {
    return (
      <BlogEditor
        post={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  if (loading) {
    return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-gold-dark" size={32} /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl text-ink">Blog Articles</h2>
          <p className="text-sm text-muted-foreground">{posts.length} article{posts.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setEditing("new")} className="flex items-center gap-2 rounded-full bg-gold hover:bg-gold-dark text-ink px-5 h-10 text-sm font-semibold transition-colors">
          <Plus size={16} /> New Article
        </button>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-xl border border-gold/15 p-3 hover:border-gold transition-colors">
            <div className="h-16 w-24 rounded-lg overflow-hidden bg-gold-soft shrink-0">
              {p.image && <img src={mediaUrl(p.image)} alt={p.heroImageAlt || p.title} className="h-full w-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="rounded-full bg-gold-soft text-gold-dark text-[11px] px-2 py-0.5 uppercase tracking-wider font-semibold">{p.category}</span>
                {p.published ? (
                  <span className="flex items-center gap-1 text-[11px] text-green-600"><Eye size={12} /> Live</span>
                ) : (
                  <span className="flex items-center gap-1 text-[11px] text-muted-foreground"><EyeOff size={12} /> Draft</span>
                )}
              </div>
              <h3 className="mt-1 font-semibold text-ink truncate">{p.title}</h3>
              <p className="text-xs text-muted-foreground">{p.date} · {p.readTime}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {p.published && (
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="grid place-items-center h-9 w-9 rounded-lg text-muted-foreground hover:bg-gold-soft hover:text-ink" title="View live">
                  <ExternalLink size={16} />
                </a>
              )}
              <button onClick={() => setEditing(p)} className="grid place-items-center h-9 w-9 rounded-lg text-muted-foreground hover:bg-gold-soft hover:text-ink" title="Edit">
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(p.id)} className="grid place-items-center h-9 w-9 rounded-lg text-muted-foreground hover:bg-red-50 hover:text-red-500" title="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
